import '../../../../components/content/ContentTitle.css';
import React, { useEffect, useRef, useState } from 'react';
import { DialogContent, DialogTitle, Slide, Fade, useMediaQuery } from '@mui/material';
import useDialogViewport from '../../../../components/common/useDialogViewport';
import useSheetHandle from '../../../../components/common/useSheetHandle';
import StableDialog from '../../../../components/common/StableDialog';
import { FiX, FiArrowUp } from 'react-icons/fi';
import AlbumActionConfirm from './AlbumActionConfirm';
import { albumReleaseDate } from './albumIntroPresentation';
import { ratingAtPosition } from './albumActionModel';
const SheetTransition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

export function RatingStar({ value = 0, size = 34, outline = false }) {
  const id = React.useId().replace(/:/g, '');
  const path =
    'M12 2L14.47 8.6L21.51 8.91L15.99 13.3L17.88 20.09L12 16.2L6.12 20.09L8.01 13.3L2.49 8.91L9.53 8.6L12 2Z';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <clipPath id={id}>
          <rect width={24 * value} height="24" />
        </clipPath>
      </defs>
      <path
        transform="translate(0 .955)"
        d={path}
        fill={outline ? 'none' : 'var(--pp-text-muted, #888)'}
        stroke={outline ? 'currentColor' : undefined}
        strokeWidth={outline ? 1.65 : undefined}
        opacity={outline ? (value >= 1 ? 0 : 1) : 0.5}
      />
      <path
        transform="translate(0 .955)"
        d={path}
        fill="var(--pp-accent, #f16438)"
        clipPath={`url(#${id})`}
      />
    </svg>
  );
}

export function AlbumRatingInput({ rating, selected, onChange, disabled, en }) {
  const dragging = useRef(false);
  const pick = (event) => {
    if (disabled) return;
    const box = event.currentTarget.getBoundingClientRect();
    onChange(ratingAtPosition(event.clientX - box.left, box.width));
  };
  return (
    <div className="pp-album-rating-input">
      <div
        className="pp-album-rating-stars"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={en ? 'Album rating' : '앨범 평점'}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={rating}
        aria-valuetext={
          selected ? rating.toFixed(1) : en ? 'Choose a star rating' : '별점을 선택해 주세요'
        }
        aria-disabled={disabled}
        onPointerDown={(e) => {
          if (disabled) return;
          dragging.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
          pick(e);
        }}
        onPointerMove={(e) => {
          if (dragging.current) pick(e);
        }}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
        onKeyDown={(e) => {
          if (disabled) return;
          const values = {
            ArrowLeft: Math.max(0, rating - 0.5),
            ArrowDown: Math.max(0, rating - 0.5),
            ArrowRight: Math.min(5, rating + 0.5),
            ArrowUp: Math.min(5, rating + 0.5),
            Home: 0,
            End: 5,
            Enter: rating,
            ' ': rating,
          };
          if (e.key in values) {
            e.preventDefault();
            onChange(values[e.key]);
          }
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <span key={i}>
            <RatingStar value={selected ? Math.max(0, Math.min(1, rating - i)) : 0} />
          </span>
        ))}
      </div>
      <p aria-live="polite">
        {selected
          ? `${en ? 'My rating' : '내 평점'} ${rating.toFixed(1)}`
          : en
            ? 'Choose a star rating'
            : '별점을 선택해 주세요'}
      </p>
    </div>
  );
}

export default function AlbumActionEditor({
  album,
  review,
  mode,
  en,
  onClose,
  onSave,
  onDelete,
  onStateChange,
  inputRef,
}) {
  const mobile = useMediaQuery('(max-width:767px)');
  const tablet = useMediaQuery('(min-width:768px) and (max-width:1279px)');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const { ref: viewportRef, onEntered, onFieldFocus } = useDialogViewport();
  const localInput = useRef(null);
  const reviewInput = inputRef || localInput;
  const baseline = useRef({
    rating: review?.rating ?? album.userRating ?? 0,
    selected: Boolean(review?.id) || Number(album.userRating) > 0,
    content: review?.content ?? '',
  }).current;
  const [draft, setDraft] = useState(baseline);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const dirty =
    draft.rating !== baseline.rating ||
    draft.selected !== baseline.selected ||
    draft.content !== baseline.content;
  useEffect(() => {
    onStateChange?.({ dirty, busy });
  }, [dirty, busy, onStateChange]);
  useEffect(() => {
    const protect = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    if (dirty || busy) window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [dirty, busy]);
  const dismiss = () => {
    if (mode !== 'review') return onClose();
    closingRef.current = true;
    reviewInput.current?.blur();
    setClosing(true);
  };
  const close = () => {
    if (busyRef.current || closingRef.current || confirmation) return false;
    if (dirty) {
      setConfirmation('discard');
      return false;
    }
    dismiss();
    return true;
  };
  const handleGesture = useSheetHandle({
    enabled: mobile && mode === 'review' && !busy && !closing && !confirmation,
    onClose: close,
    root: viewportRef,
  });
  const run = async (action, operation = 'save') => {
    if (busyRef.current || closingRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await action();
      if (mounted.current) dismiss();
    } catch {
      if (mounted.current) setError(operation);
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const remove = () => {
    if (busyRef.current || closingRef.current) return;
    setError(null);
    setConfirmation(mode);
  };
  const heading =
    mode === 'rating'
      ? en
        ? 'Rate Album'
        : '앨범 평가'
      : review?.content?.trim()
        ? en
          ? 'Edit Review'
          : '리뷰 수정'
        : en
          ? 'Write a Review'
          : '리뷰 작성';
  return (
    <>
      <StableDialog
        open={!closing}
        data-review-existing={Boolean(review?.id)}
        onClose={close}
        fullWidth
        maxWidth="sm"
        className={`pp-album-dialog pp-album-editor-dialog pp-album-editor-${mode}${closing ? ' pp-editor-closing' : ''}${tablet ? ' pp-editor-tablet' : ''}`}
        ref={viewportRef}
        TransitionComponent={(mobile || tablet) && !reduceMotion ? SheetTransition : Fade}
        transitionDuration={
          mode === 'review' ? (reduceMotion ? 0 : { enter: 320, exit: 220 }) : undefined
        }
        TransitionProps={{
          ...((mobile || tablet) && !reduceMotion
            ? {
                easing: {
                  enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
                  exit: 'cubic-bezier(0.4, 0, 1, 1)',
                },
              }
            : {}),
          onEntered,
          onExited: mode === 'review' ? onClose : undefined,
        }}
        aria-labelledby="album-editor-title"
      >
        {mode === 'review' && mobile && (
          <button
            type="button"
            className="pp-review-sheet-handle"
            aria-label={
              en ? 'Close review sheet (drag down)' : '리뷰 작성창 닫기 핸들 (아래로 쓸기)'
            }
            disabled={busy || closing || Boolean(confirmation)}
            onClick={(event) => {
              if (event.detail === 0) close();
            }}
            {...handleGesture}
          >
            <span />
          </button>
        )}
        <DialogTitle
          id="album-editor-title"
          className="pp-album-dialog-title"
          {...(mode === 'review' && mobile ? handleGesture : {})}
        >
          {mode === 'review' ? (
            <>
              <div className="pp-review-editor-actions">
                <button
                  type="button"
                  onClick={close}
                  disabled={busy || closing}
                  aria-label={en ? 'Close' : '닫기'}
                >
                  <FiX size={22} />
                </button>
                <div>
                  {review?.id && (
                    <button type="button" disabled={busy || closing} onClick={remove}>
                      {en ? 'Delete Review' : '리뷰 삭제'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="pp-review-header-submit"
                    aria-label={
                      busy
                        ? en
                          ? 'Saving…'
                          : '저장 중…'
                        : review?.content?.trim()
                          ? en
                            ? 'Edit Review'
                            : '리뷰 수정'
                          : en
                            ? 'Publish Review'
                            : '리뷰 등록'
                    }
                    disabled={busy || closing || !draft.selected || !draft.content.trim()}
                    onClick={() => run(() => onSave(draft))}
                  >
                    <FiArrowUp size={26} aria-hidden="true" />
                  </button>
                </div>
              </div>
              <span>{heading}</span>
            </>
          ) : (
            <>
              {heading}
              <button
                type="button"
                onClick={close}
                disabled={busy || closing}
                aria-label={en ? 'Close' : '닫기'}
              >
                <FiX size={20} />
              </button>
            </>
          )}
        </DialogTitle>
        <DialogContent className="pp-album-editor">
          <div className="pp-album-editor-summary">
            {album.imageUrl && <img src={album.imageUrl} alt="" />}
            <div>
              <strong>
                {album.title}
                {album.contentRating === 'EXPLICIT' && (
                  <span className="pp-album-editor-explicit pp-inline-explicit">E</span>
                )}
              </strong>
              <p>{album.artist?.name}</p>
              <small>
                {mode === 'review' && album.releaseDate
                  ? albumReleaseDate(album.releaseDate)
                  : album.reviewCount > 0
                    ? en
                      ? `Overall average ${Number(album.averageRating || 0).toFixed(1)} · ${album.reviewCount} ratings`
                      : `전체 평균 ${Number(album.averageRating || 0).toFixed(1)} · ${album.reviewCount}명 참여`
                    : en
                      ? 'No album ratings yet.'
                      : '아직 등록된 앨범 평점이 없습니다.'}
              </small>
            </div>
          </div>
          {mode === 'rating' && (
            <h3>{en ? 'What did you think of this album?' : '이 앨범은 어땠나요?'}</h3>
          )}
          <AlbumRatingInput
            {...draft}
            disabled={busy || closing}
            en={en}
            onChange={(rating) => setDraft((d) => ({ ...d, rating, selected: true }))}
          />
          {mode === 'review' && (
            <label className="pp-album-review-field">
              {en ? 'Review' : '리뷰'}
              <span className="pp-review-input-shell">
                <textarea
                  ref={reviewInput}
                  onFocus={onFieldFocus}
                  placeholder={
                    en
                      ? 'Share what you discovered while listening.'
                      : '앨범을 들으며 발견한 생각을 남겨주세요.'
                  }
                  rows={4}
                  maxLength={10000}
                  value={draft.content}
                  disabled={busy || closing}
                  onChange={(e) => setDraft((d) => ({ ...d, content: e.target.value }))}
                />
                <small>{draft.content.length} / 10000</small>
              </span>
            </label>
          )}
          {error === 'save' && (
            <p role="alert">
              {en
                ? 'Could not save. Your draft is kept. Please try again.'
                : '저장하지 못했어요. 작성 내용은 유지됩니다. 다시 시도해 주세요.'}
            </p>
          )}
          {mode === 'rating' && (
            <div className="pp-album-editor-buttons">
              {review?.id && (
                <button type="button" disabled={busy || closing} onClick={remove}>
                  {mode === 'rating'
                    ? en
                      ? 'Delete Rating'
                      : '평가 삭제'
                    : en
                      ? 'Delete Review'
                      : '리뷰 삭제'}
                </button>
              )}
              <button
                type="button"
                className="pp-album-submit"
                disabled={
                  busy || closing || !draft.selected || (mode === 'review' && !draft.content.trim())
                }
                onClick={() => run(() => onSave(draft))}
              >
                {busy
                  ? en
                    ? 'Saving…'
                    : '저장 중…'
                  : mode === 'rating'
                    ? review?.id
                      ? en
                        ? 'Update Rating'
                        : '평점 수정'
                      : en
                        ? 'Save Rating'
                        : '평점 저장'
                    : review?.content?.trim()
                      ? en
                        ? 'Edit Review'
                        : '리뷰 수정'
                      : en
                        ? 'Publish Review'
                        : '리뷰 등록'}
              </button>
            </div>
          )}
        </DialogContent>
      </StableDialog>
      {confirmation && (
        <AlbumActionConfirm
          kind={confirmation}
          hasReview={Boolean(review?.content?.trim())}
          en={en}
          busy={busy}
          error={error === 'delete'}
          onCancel={() => {
            if (!busyRef.current) {
              setConfirmation(null);
              setError(null);
            }
          }}
          onConfirm={() => {
            if (confirmation === 'discard') {
              setConfirmation(null);
              dismiss();
            } else run(onDelete, 'delete');
          }}
        />
      )}
    </>
  );
}
